import React, { useState } from "react";
import styled from "styled-components";

import SecureScreenContainer from "../components/common/containers/ScreenContainer";
import Header from "../components/common/Header";
import Button from "../components/common/Button";
import Modal from "../components/common/Modal";
import AccountAddForm from "../components/forms/AccountAddForm";
import PlaidLinkButton from "../components/plaid/PlaidLinkButton";
import { useAccounts } from "../hooks/useAccounts";
import { theme } from "../defs/theme";
import formatMoney from "../utils/formatMoney";
import TrashIcon from "../resources/images/trash.svg";
import PlusIcon from "../resources/images/plusIcon.svg";

const AccountsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const HeaderSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 16px;
`;

const ActionButtons = styled.div`
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
`;

const AccountsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const AccountCard = styled.div`
    background: ${theme.palette.background.main};
    border-radius: 8px;
    padding: 20px;
    ${theme.shadow.med};
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
`;

const AccountInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    min-width: 200px;
`;

const AccountName = styled.div`
    font-size: ${theme.font.size.big};
    font-weight: ${theme.font.weight.bold};
    color: ${theme.palette.background.contrast};
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
`;

const AccountDetails = styled.div`
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
    font-size: ${theme.font.size.small};
    color: ${theme.palette.background.contrast};
    opacity: 0.7;
`;

const AccountBalance = styled.div<{ negative?: boolean }>`
    font-size: ${theme.font.size.bigger};
    font-weight: ${theme.font.weight.bold};
    color: ${(props) => (props.negative ? theme.palette.error.main : theme.palette.background.contrast)};
`;

const AccountActions = styled.div`
    display: flex;
    gap: 8px;
    align-items: center;
`;

const DeleteButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: background-color 0.2s;

    &:hover {
        background-color: ${theme.palette.error.light};
    }

    &:active {
        background-color: ${theme.palette.error.main};
    }

    svg {
        width: 20px;
        height: 20px;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const PlaidBadge = styled.span`
    background: ${theme.palette.primary.main};
    color: ${theme.palette.primary.contrast};
    padding: 4px 8px;
    border-radius: 4px;
    font-size: ${theme.font.size.smallest};
    font-weight: ${theme.font.weight.bold};
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 48px 24px;
    color: ${theme.palette.background.contrast};
    opacity: 0.6;
    font-size: ${theme.font.size.big};
`;

const ErrorMessage = styled.div`
    background: ${theme.palette.error.light};
    color: ${theme.palette.error.main};
    padding: 12px;
    border-radius: 8px;
    font-size: ${theme.font.size.small};
`;

const LoadingMessage = styled.div`
    text-align: center;
    padding: 24px;
    color: ${theme.palette.background.contrast};
    opacity: 0.6;
`;

export default function AccountsScreen(): JSX.Element {
    const { allAccounts, isLoading, error, deleteAccount, refreshAccounts } = useAccounts();
    const [showAddModal, setShowAddModal] = useState(false);
    const [deletingId, setDeletingId] = useState<number | null>(null);

    const handleDelete = async (accountId: number) => {
        if (window.confirm("Are you sure you want to delete this account?")) {
            setDeletingId(accountId);
            try {
                await deleteAccount(accountId);
            } finally {
                setDeletingId(null);
            }
        }
    };

    const handleAddSuccess = () => {
        setShowAddModal(false);
    };

    const handlePlaidLinked = async () => {
        await refreshAccounts();
    };

    const formatAccountBalance = (balance: number): string => {
        const formatted = formatMoney(Math.abs(balance), 2);
        return balance < 0 ? `-$${formatted}` : `$${formatted}`;
    };

    return (
        <SecureScreenContainer>
            <AccountsContainer>
                <HeaderSection>
                    <Header>Accounts</Header>
                    <ActionButtons>
                        <Button icon={<PlusIcon />} onClick={() => setShowAddModal(true)}>
                            Add Account
                        </Button>
                        <PlaidLinkButton onLinked={handlePlaidLinked} />
                    </ActionButtons>
                </HeaderSection>

                {error && <ErrorMessage>{error}</ErrorMessage>}

                {isLoading ? (
                    <LoadingMessage>Loading accounts...</LoadingMessage>
                ) : allAccounts.length === 0 ? (
                    <EmptyState>No accounts yet. Add an account to get started!</EmptyState>
                ) : (
                    <AccountsList>
                        {allAccounts.map((account) => (
                            <AccountCard key={account.id}>
                                <AccountInfo>
                                    <AccountName>
                                        {account.name}
                                        {account.isPlaidLinked && <PlaidBadge>Plaid</PlaidBadge>}
                                    </AccountName>
                                    <AccountDetails>
                                        <span>{account.accountType || "checking"}</span>
                                    </AccountDetails>
                                </AccountInfo>
                                <AccountBalance negative={account.cachedBalance < 0}>
                                    {formatAccountBalance(account.cachedBalance)}
                                </AccountBalance>
                                <AccountActions>
                                    <DeleteButton
                                        onClick={() => account.id && handleDelete(account.id)}
                                        disabled={deletingId === account.id}
                                        title="Delete account"
                                    >
                                        <TrashIcon />
                                    </DeleteButton>
                                </AccountActions>
                            </AccountCard>
                        ))}
                    </AccountsList>
                )}

                <Modal visible={showAddModal} onClose={() => setShowAddModal(false)}>
                    <AccountAddForm onSubmit={handleAddSuccess} />
                </Modal>
            </AccountsContainer>
        </SecureScreenContainer>
    );
}
